// The music engine: game state in, sound out.
//
//   setState / cue ──▶ conductor (what to play) ──▶ beds (loops) ──▶ music bus ─▶ duck ─┐
//                                                    stings ───────▶ effects bus ────────┴▶ out
//
// Nothing plays before the first user gesture. Beds are loaded only when a state asks for them;
// stings are decoded in the background once sound is allowed, since they are small.

import { isBlocked, loadBed, type Bed } from './beds.ts';
import { judgeSting, planBed, stingFor, type BedPlaying } from './conductor.ts';
import { dbToGain, fadeTo, still } from './fader.ts';
import type { Fade } from './fade.ts';
import { cueOf, fileFor, parseManifest, urlFor, type FileEntry, type Manifest } from './manifest.ts';
import { busGains, loadSettings, saveSettings } from './settings.ts';
import {
  SCREENS,
  SOURCES,
  STING_CUES,
  type BedCue,
  type Channel,
  type Music,
  type MusicEvent,
  type MusicSnapshot,
  type MusicState,
  type MusicStatus,
  type MuteMode,
  type Screen,
  type Settings,
  type Source,
  type StingCue,
} from './types.ts';
import type { BufferLike, ContextLike, Deps, GainLike } from './webaudio.ts';

/** One cue replacing another, and the first bed coming in from silence. */
export const CUE_CROSSFADE_S = 3;

/** Music drops by this much while a sting sounds, for DUCK_HOLD_S. */
export const DUCK_DB = -6;
export const DUCK_HOLD_S = 1;

const DUCK_ATTACK_S = 0.05;
const DUCK_RELEASE_S = 0.6;

// Volume sliders ramp instead of stepping, which would click.
const BUS_RAMP_S = 0.05;

const STING_CUT_S = 0.1;
const CUT_MARGIN_S = 0.1;

// A sting still loading after this long is stale; playing it would mislead.
const STING_LATE_S = 1.5;

const MANIFEST_FILE = 'manifest.json';
const MS_PER_S = 1000;

const INITIAL_STATE: MusicState = { screen: 'loading', atWar: false };

interface Graph {
  ctx: ContextLike;
  music: GainLike;
  duck: GainLike;
  effects: GainLike;
  fades: { music: Fade; duck: Fade; effects: Fade };
}

interface PlayingBed extends BedPlaying {
  bed: Bed;
  /** The file, so the same one is never restarted, whichever cue asks for it. */
  key: string;
  token: number;
}

interface Sting {
  cue: StingCue;
  endsAt: number;
  /** Fade out quickly, then stop. */
  cut(): void;
  stop(): void;
}

function noop(): void {}

export function createEngine(deps: Deps): Music {
  let settings: Settings = loadSettings(deps.storage);
  let state: MusicState = { ...INITIAL_STATE };
  let status: MusicStatus = 'locked';
  let manifest: Manifest | null = null;
  let graph: Graph | null = null;

  let playing: PlayingBed | null = null;
  const leaving = new Set<Bed>();

  // The key of the bed last asked for, and a counter that cancels loads that are no longer wanted.
  let wanted: string | null = null;
  let generation = 0;

  let sting: Sting | null = null;
  const buffers = new Map<string, Promise<BufferLike | null>>();

  let refreshTimer: number | null = null;
  let duckTimer: number | null = null;
  const cutTimers = new Set<number>();

  let disposed = false;
  let offGesture: (() => void) | null = null;
  const listeners = new Set<() => void>();
  let snapshot = makeSnapshot();

  function makeSnapshot(): MusicSnapshot {
    return { settings: { ...settings }, status, bed: playing?.cue ?? null };
  }

  function publish(): void {
    snapshot = makeSnapshot();
    listeners.forEach((listener) => listener());
  }

  // ─── Sound on: gesture, graph, manifest ────────────────────────────────────────────────

  function listen(): void {
    offGesture ??= deps.onGesture(unlock);
  }

  function unlisten(): void {
    offGesture?.();
    offGesture = null;
  }

  function buildGraph(ctx: ContextLike): Graph {
    const now = ctx.currentTime;
    const music = ctx.createGain();
    const duck = ctx.createGain();
    const effects = ctx.createGain();
    music.connect(duck);
    duck.connect(ctx.destination);
    effects.connect(ctx.destination);

    const gains = busGains(settings);
    music.gain.setValueAtTime(gains.music, now);
    effects.gain.setValueAtTime(gains.effects, now);
    duck.gain.setValueAtTime(1, now);

    return { ctx, music, duck, effects, fades: { music: still(gains.music, now), duck: still(1, now), effects: still(gains.effects, now) } };
  }

  function unlock(): void {
    if (disposed || status === 'ready' || status === 'unavailable') {
      return;
    }

    if (graph === null) {
      const ctx = deps.createContext();
      if (ctx === null) {
        unlisten();
        status = 'unavailable';
        publish();
        return;
      }
      graph = buildGraph(ctx);
    }

    // A context made inside a gesture is allowed to run; resume() covers one that started suspended.
    // A gesture that gives no user activation (the Escape key) leaves it suspended: the listeners stay
    // and the next gesture asks again, instead of showing 'ready' over silence.
    const { ctx } = graph;
    ctx.resume().then(() => start(ctx), noop);
  }

  /** The context runs: sound is on. Several events of one click all land here; the first one wins. */
  function start(ctx: ContextLike): void {
    if (disposed || status !== 'locked' || graph?.ctx !== ctx || !ctx.running) {
      return;
    }

    unlisten();
    status = 'ready';
    publish();
    refresh();
    warm();
  }

  async function loadManifest(): Promise<void> {
    let parsed: Manifest;
    try {
      parsed = parseManifest(await deps.fetchJson(urlFor(deps.baseUrl, MANIFEST_FILE)));
    } catch {
      status = 'unavailable';
      publish();
      return;
    }
    if (disposed) {
      return;
    }

    manifest = parsed;
    refresh();
    warm();
  }

  // ─── Beds ──────────────────────────────────────────────────────────────────────────────

  /** Pick the bed for the current state and switch to it, now or after the dwell. */
  function refresh(): void {
    clearRefresh();
    if (disposed || status !== 'ready' || graph === null || manifest === null) {
      return;
    }

    const now = graph.ctx.currentTime;
    const plan = planBed(state, playing, now);
    if (plan.at > now) {
      refreshTimer = deps.setTimer(refresh, (plan.at - now) * MS_PER_S);
      return;
    }

    const file = fileFor(manifest, plan.cue, settings.source);
    if (file === null) {
      return;
    }

    const key = file.file;
    if (key === wanted) {
      return;
    }

    // Back to what already plays: just cancel whatever was loading.
    generation += 1;
    wanted = key;
    if (playing?.key === key) {
      return;
    }
    void switchTo(plan.cue, state.screen, file, key, generation);
  }

  function clearRefresh(): void {
    if (refreshTimer !== null) {
      deps.clearTimer(refreshTimer);
    }
    refreshTimer = null;
  }

  async function switchTo(cue: BedCue, screen: Screen, file: FileEntry, key: string, token: number): Promise<void> {
    const g = graph;
    if (g === null) {
      return;
    }

    let bed: Bed;
    try {
      bed = await loadBed(
        { ctx: g.ctx, deps, out: g.music },
        { url: urlFor(deps.baseUrl, file.file), seconds: file.seconds, crossfade: file.crossfade ?? CUE_CROSSFADE_S, onFail: () => drop(token) },
      );
    } catch (error) {
      // Otherwise the current bed plays on and `wanted` stops this file being retried in a loop.
      if (token === generation && isBlocked(error)) {
        lock();
      }
      return;
    }

    if (token !== generation || disposed) {
      bed.stop();
      return;
    }

    const previous = playing;
    playing = { bed, cue, screen, key, seconds: file.seconds, since: g.ctx.currentTime, token };
    bed.start(CUE_CROSSFADE_S);
    if (previous !== null) {
      leaving.add(previous.bed);
      previous.bed.release(CUE_CROSSFADE_S, () => leaving.delete(previous.bed));
    }
    publish();

    // Plan what comes after this bed: a held war flip, or the sandbox's next pass.
    refresh();
  }

  // The playing file broke: go silent rather than loop on an error.
  function drop(token: number): void {
    if (playing === null || playing.token !== token) {
      return;
    }
    playing.bed.stop();
    playing = null;
    publish();
  }

  // The browser refused to play: wait for the next gesture, then start over.
  function lock(): void {
    generation += 1;
    wanted = null;
    playing?.bed.stop();
    playing = null;
    status = 'locked';
    listen();
    publish();
  }

  // ─── Stings ────────────────────────────────────────────────────────────────────────────

  function bufferOf(url: string): Promise<BufferLike | null> {
    const known = buffers.get(url);
    if (known !== undefined) {
      return known;
    }

    const loading = (async () => {
      if (graph === null) {
        return null;
      }
      return graph.ctx.decodeAudioData(await deps.fetchBytes(url));
    })().catch(() => {
      buffers.delete(url);
      return null;
    });
    buffers.set(url, loading);
    return loading;
  }

  /** Decode every sting in the background so the first one is not late. */
  function warm(): void {
    if (graph === null || manifest === null) {
      return;
    }
    for (const cue of STING_CUES) {
      const file = fileFor(manifest, cue, settings.source);
      if (file !== null) {
        void bufferOf(urlFor(deps.baseUrl, file.file));
      }
    }
  }

  function duck(g: Graph): void {
    if (duckTimer !== null) {
      deps.clearTimer(duckTimer);
    }

    g.fades.duck = fadeTo(g.duck.gain, g.fades.duck, dbToGain(DUCK_DB), g.ctx.currentTime, DUCK_ATTACK_S);
    duckTimer = deps.setTimer(() => {
      duckTimer = null;
      g.fades.duck = fadeTo(g.duck.gain, g.fades.duck, 1, g.ctx.currentTime, DUCK_RELEASE_S);
    }, DUCK_HOLD_S * MS_PER_S);
  }

  function sound(g: Graph, cue: StingCue, trimDb: number, buffer: BufferLike): void {
    const now = g.ctx.currentTime;
    const level = dbToGain(trimDb);
    const trim = g.ctx.createGain();
    trim.gain.setValueAtTime(level, now);
    const source = g.ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(trim);
    trim.connect(g.effects);

    // A bigger sting replaces a smaller one that is still sounding.
    sting?.cut();
    const cut = (): void => {
      fadeTo(trim.gain, still(level, now), 0, g.ctx.currentTime, STING_CUT_S);
      const id = deps.setTimer(() => {
        cutTimers.delete(id);
        source.stop();
      }, (STING_CUT_S + CUT_MARGIN_S) * MS_PER_S);
      cutTimers.add(id);
    };
    sting = { cue, endsAt: now + buffer.duration, cut, stop: () => source.stop() };

    source.onended = () => {
      source.disconnect();
      trim.disconnect();
    };
    source.start();
    duck(g);
  }

  async function play(cue: StingCue): Promise<void> {
    const g = graph;
    if (disposed || status !== 'ready' || g === null || manifest === null) {
      return;
    }

    const asked = g.ctx.currentTime;
    if (judgeSting(cue, sting, asked) === 'drop') {
      return;
    }

    const entry = cueOf(manifest, cue);
    const file = fileFor(manifest, cue, settings.source);
    if (entry === null || file === null) {
      return;
    }

    const buffer = await bufferOf(urlFor(deps.baseUrl, file.file));
    if (buffer === null || disposed || graph !== g) {
      return;
    }

    // Time has passed: ask again, and skip a sting that is no longer about now.
    const now = g.ctx.currentTime;
    if (now - asked > STING_LATE_S || judgeSting(cue, sting, now) === 'drop') {
      return;
    }
    sound(g, cue, entry.gainDb, buffer);
  }

  // ─── Settings ──────────────────────────────────────────────────────────────────────────

  function applyBuses(): void {
    if (graph === null) {
      return;
    }

    const gains = busGains(settings);
    const now = graph.ctx.currentTime;
    graph.fades.music = fadeTo(graph.music.gain, graph.fades.music, gains.music, now, BUS_RAMP_S);
    graph.fades.effects = fadeTo(graph.effects.gain, graph.fades.effects, gains.effects, now, BUS_RAMP_S);
  }

  function changed(): void {
    saveSettings(deps.storage, settings);
    applyBuses();
    publish();
  }

  // ─── Public API ────────────────────────────────────────────────────────────────────────

  const music: Music = {
    setState(next) {
      if (disposed) {
        return;
      }

      const screen = next.screen !== undefined && SCREENS.includes(next.screen) ? next.screen : state.screen;
      state = { screen, atWar: next.atWar ?? state.atWar };
      refresh();
    },

    cue(event: MusicEvent) {
      void play(stingFor(event));
    },

    setVolume(channel: Channel, value: number) {
      if (disposed || !Number.isFinite(value)) {
        return;
      }

      const next = Math.min(1, Math.max(0, value));
      if (settings[channel] === next) {
        return;
      }
      settings = { ...settings, [channel]: next };
      changed();
    },

    setSource(source: Source) {
      if (disposed || !SOURCES.includes(source) || source === settings.source) {
        return;
      }

      settings = { ...settings, source };
      changed();
      refresh();
      warm();
    },

    mute(mode: MuteMode) {
      if (disposed) {
        return;
      }

      const muted = mode === 'toggle' ? !settings.muted : mode === 'on';
      if (muted === settings.muted) {
        return;
      }
      settings = { ...settings, muted };
      changed();
    },

    getSnapshot: () => snapshot,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    unlock,

    dispose() {
      if (disposed) {
        return;
      }

      disposed = true;
      unlisten();
      clearRefresh();
      if (duckTimer !== null) {
        deps.clearTimer(duckTimer);
      }
      cutTimers.forEach((id) => deps.clearTimer(id));
      cutTimers.clear();

      playing?.bed.stop();
      playing = null;
      sting?.stop();
      sting = null;
      [...leaving].forEach((bed) => bed.stop());
      leaving.clear();
      graph?.ctx.close().catch(noop);
      listeners.clear();
    },
  };

  listen();
  void loadManifest();
  return music;
}
